import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ResponsiveContainer,
} from "recharts";
import {
  FiActivity,
  FiUsers,
  FiGlobe,
  FiClock,
  FiTrendingUp,
  FiEye,
  FiX,
  FiArrowUp,
  FiArrowDown,
} from "react-icons/fi";
import { FaSort } from "react-icons/fa";
import CryptoJS from "crypto-js";
import Navbar from "../../../common/components/HeaderOpsPage/Navbar";
import Body from "../../../common/components/Body/Body";
import "./AnalyticsScreen.css";

const COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
];

const METHOD_COLORS = {
  GET: "#3b82f6",
  POST: "#10b981",
  PUT: "#f59e0b",
  DELETE: "#ef4444",
  PATCH: "#8b5cf6",
};

// ── Heatmap helpers ──────────────────────────────────────────────
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function buildHeatmap(activityTable) {
  // Build a 7-day × 4-week grid from activity timestamps
  const counts = {};
  (activityTable || []).forEach((row) => {
    const d = new Date(row.timestamp);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    counts[key] = (counts[key] || 0) + 1;
  });

  // Last 28 days
  const today = new Date();
  const weeks = [[], [], [], []]; // 4 weeks
  for (let i = 27; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const weekIdx = Math.floor((27 - i) / 7);
    weeks[weekIdx].push({ date: d, count: counts[key] || 0 });
  }
  return weeks;
}

function heatColor(count, max) {
  if (count === 0) return "#e2e8f0";
  const ratio = count / (max || 1);
  if (ratio < 0.25) return "#bfdbfe";
  if (ratio < 0.5) return "#60a5fa";
  if (ratio < 0.75) return "#2563eb";
  return "#1e3a8a";
}

// ── Sparkline (tiny bar inside stat card) ───────────────────────
function Sparkline({ data, color }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data);
  const w = 6,
    gap = 2,
    h = 32;
  return (
    <svg
      width={(w + gap) * data.length}
      height={h}
      style={{ display: "block", marginTop: 8 }}
    >
      {data.map((v, i) => {
        const barH = max ? Math.max(3, (v / max) * h) : 3;
        return (
          <rect
            key={i}
            x={i * (w + gap)}
            y={h - barH}
            width={w}
            height={barH}
            rx={2}
            fill={color}
            opacity={i === data.length - 1 ? 1 : 0.35}
          />
        );
      })}
    </svg>
  );
}

// ── Method badge ─────────────────────────────────────────────────
function MethodBadge({ method }) {
  const colors = {
    GET: { bg: "#eff6ff", text: "#2563eb" },
    POST: { bg: "#f0fdf4", text: "#16a34a" },
    PUT: { bg: "#fffbeb", text: "#d97706" },
    DELETE: { bg: "#fef2f2", text: "#dc2626" },
    PATCH: { bg: "#f5f3ff", text: "#7c3aed" },
  };
  const c = colors[(method || "").toUpperCase()] || {
    bg: "#f1f5f9",
    text: "#475569",
  };
  return (
    <span
      style={{
        background: c.bg,
        color: c.text,
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.03em",
      }}
    >
      {method}
    </span>
  );
}

// ── Main Component ───────────────────────────────────────────────
const AnalyticsScreen = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [timeRange, setTimeRange] = useState("Last 30 days");
  const [environment, setEnvironment] = useState("All");

  // Traffic chart tab: "hits" | "users" | "errors"
  const [trafficTab, setTrafficTab] = useState("hits");

  // Top users toggle: "hits" | "name"
  const [userSort, setUserSort] = useState("hits");

  // Activity table method filter
  const [methodFilter, setMethodFilter] = useState("All");

  // Activity table sorting & pagination
  const [sortField, setSortField] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Feedback search
  const [feedbackSearchTerm, setFeedbackSearchTerm] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const authToken = localStorage.getItem("authToken");
  const userRole = useSelector(
    (state) => state.login?.login?.data?.data?.[0]?.Role,
  );

  useEffect(() => {
    if (userRole !== "Admin") {
      setError("Access denied. Admin role required.");
      setLoading(false);
      return;
    }
    fetchAnalytics();
  }, [userRole]);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_ANALYTICS_API}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch analytics data");
      const encryptedData = await response.text();
      const decryptedData = decryptFunction(
        encryptedData,
        "1234567890abcdef1234567890abcdef",
      );
      console.log(decryptedData, "Rohit checking analyicts");
      setAnalyticsData(decryptedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const decryptFunction = (ciphertext, key) => {
    const bytes = CryptoJS.AES.decrypt(ciphertext, key);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  };

  // ── Loading ──
  if (loading) {
    return (
      <div>
        <Navbar />
        <Body>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              gap: 16,
            }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ color: "#64748b" }}>
              Preparing your analytics dashboard…
            </p>
          </div>
        </Body>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div>
        <Navbar />
        <Body>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              gap: 12,
            }}
          >
            <FiActivity size={48} color="#ef4444" />
            <h2 style={{ fontSize: 20, fontWeight: 700 }}>
              Dashboard Access Error
            </h2>
            <p style={{ color: "#64748b" }}>{error}</p>
          </div>
        </Body>
      </div>
    );
  }

  const {
    summary,
    topUsers,
    timeTrends,
    dailyTrends,
    endpointDistribution,
    methodDistribution,
    activityTable,
    feedbackTable,
    feedbackStats,
  } = analyticsData;

  // Sparkline data from dailyTrends
  const sparkHits = (dailyTrends || []).map((d) => d.hits || 0);

  // Top users sorted
  const sortedUsers = [...(topUsers || [])].sort((a, b) =>
    userSort === "hits"
      ? b.hits - a.hits
      : a.FirstName.localeCompare(b.FirstName),
  );

  // Heatmap
  const heatmapWeeks = buildHeatmap(activityTable);
  const heatMax = Math.max(...heatmapWeeks.flat().map((c) => c.count));

  // Filtered activity table
  const filteredActivity = (activityTable || []).filter(
    (r) => methodFilter === "All" || r.Method === methodFilter,
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleMethodFilter = (m) => {
    setMethodFilter(m);
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span
          style={{
            display: "inline-flex",
            marginLeft: 5,
            opacity: 0.35,
            verticalAlign: "middle",
          }}
        >
          <FaSort size={12} />
        </span>
      );
    }
    return (
      <span
        style={{
          display: "inline-flex",
          marginLeft: 5,
          color: "#3b82f6",
          verticalAlign: "middle",
        }}
      >
        {sortDirection === "asc" ? (
          <FiArrowUp size={13} />
        ) : (
          <FiArrowDown size={13} />
        )}
      </span>
    );
  };

  const sortedActivity = [...filteredActivity].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case "date":
      case "time":
        aVal = new Date(a.timestamp).getTime();
        bVal = new Date(b.timestamp).getTime();
        break;
      case "user":
        aVal = (a.FirstName || "").toLowerCase();
        bVal = (b.FirstName || "").toLowerCase();
        break;
      case "endpoint":
        aVal = (a.Endpoint || "").toLowerCase();
        bVal = (b.Endpoint || "").toLowerCase();
        break;
      case "method":
        aVal = (a.Method || "").toLowerCase();
        bVal = (b.Method || "").toLowerCase();
        break;
      case "status":
        aVal = a.status || 200;
        bVal = b.status || 200;
        break;
      default:
        aVal = new Date(a.timestamp).getTime();
        bVal = new Date(b.timestamp).getTime();
    }
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(sortedActivity.length / rowsPerPage),
  );
  const paginatedActivity = sortedActivity.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  // Traffic chart data key based on tab
  const trafficDataKey =
    trafficTab === "hits"
      ? "hits"
      : trafficTab === "users"
        ? "users"
        : "errors";

  // Method distribution for donut legend labels with %
  const totalMethodHits = (methodDistribution || []).reduce(
    (s, m) => s + m.value,
    0,
  );

  return (
    <div>
      <Navbar />
      <Body>
        <div
          style={{
            padding: "24px 28px",
            background: "#f8fafc",
            minHeight: "100vh",
            fontFamily: "'DM Sans', 'Inter', sans-serif",
          }}
        >
          {/* ── Filters ── */}
          <div
            style={{
              display: "flex",
              gap: 24,
              marginBottom: 24,
              flexWrap: "wrap",
            }}
          >
            <div>
              <label style={styles.filterLabel}>Time range:</label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                style={styles.select}
              >
                <option>Last 7 days</option>
                <option>Last 30 days</option>
                <option>Last 90 days</option>
              </select>
            </div>
            <div>
              <label style={styles.filterLabel}>Environment:</label>
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                style={styles.select}
              >
                <option>All</option>
                <option>Production</option>
                <option>Staging</option>
              </select>
            </div>
          </div>

          {/* ── Summary Cards ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 16,
              marginBottom: 20,
            }}
          >
            <StatCard
              title="Total API Hits"
              value={summary.totalAPIHits?.toLocaleString()}
              trend="+12.4% vs prev period"
              trendType="up"
              sparkData={sparkHits}
              sparkColor="#3b82f6"
              highlighted
            />
            <StatCard
              title="Active Users"
              value={summary.activeUsers}
              trend="+8 new this period"
              trendType="up"
              sparkData={sparkHits.map((v) => Math.round(v / 50))}
              sparkColor="#3b82f6"
            />
            <StatCard
              title="Active Endpoints"
              value={summary.activeEndpoints}
              trend="-2 deprecated"
              trendType="down"
              sparkData={sparkHits.map((v) => Math.round(v / 300))}
              sparkColor="#3b82f6"
            />
            <StatCard
              title="Avg Response Time"
              value={
                summary.avgResponseTime ? `${summary.avgResponseTime}ms` : "—"
              }
              trend="+11ms slower"
              trendType="warn"
              sparkData={sparkHits.map((v) => Math.round(v / 60))}
              sparkColor="#3b82f6"
            />
          </div>

          {/* ── Traffic Trends + HTTP Methods ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            {/* Traffic Trends */}
            <div style={styles.card}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 16,
                }}
              >
                <h3 style={styles.cardTitle}>API Traffic Trends</h3>
                <div style={{ display: "flex", gap: 6 }}>
                  {["hits", "users", "errors"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setTrafficTab(tab)}
                      style={{
                        ...styles.tabBtn,
                        background:
                          trafficTab === tab ? "#1e293b" : "transparent",
                        color: trafficTab === tab ? "#fff" : "#64748b",
                        borderColor: trafficTab === tab ? "#1e293b" : "#e2e8f0",
                      }}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={dailyTrends || []}>
                  <defs>
                    <linearGradient
                      id="trafficGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#3b82f6"
                        stopOpacity={0.15}
                      />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94a3b8", fontSize: 11 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94a3b8", fontSize: 11 }}
                  />
                  <Tooltip contentStyle={styles.tooltip} />
                  <Area
                    type="monotone"
                    dataKey={trafficDataKey}
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#trafficGrad)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* HTTP Methods Donut */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>HTTP Methods</h3>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px 16px",
                  marginBottom: 8,
                }}
              >
                {(methodDistribution || []).map((m, i) => (
                  <span
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: COLORS[i % COLORS.length],
                        display: "inline-block",
                      }}
                    />
                    {m.name}{" "}
                    {totalMethodHits
                      ? Math.round((m.value / totalMethodHits) * 100)
                      : 0}
                    %
                  </span>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={methodDistribution || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    dataKey="value"
                    paddingAngle={2}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {(methodDistribution || []).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={styles.tooltip} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Feedback Distribution + Top Users ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            {/* Call Feedback Distribution */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Call Feedback Distribution</h3>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px 16px",
                  marginBottom: 8,
                }}
              >
                {(feedbackStats || []).map((f, i) => (
                  <span
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: COLORS[i % COLORS.length],
                        display: "inline-block",
                      }}
                    />
                    {f.name}: {f.value}
                  </span>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={feedbackStats || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    dataKey="value"
                    paddingAngle={3}
                  >
                    {(feedbackStats || []).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={styles.tooltip} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Top Users */}
            <div style={styles.card}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 14,
                }}
              >
                <h3 style={styles.cardTitle}>Top Users</h3>
                <div style={{ display: "flex", gap: 6 }}>
                  {["hits", "name"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setUserSort(s)}
                      style={{
                        ...styles.tabBtn,
                        background: userSort === s ? "#1e293b" : "transparent",
                        color: userSort === s ? "#fff" : "#64748b",
                        borderColor: userSort === s ? "#1e293b" : "#e2e8f0",
                      }}
                    >
                      By {s}
                    </button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer
                width="100%"
                height={Math.max(sortedUsers.length * 32, 240)}
              >
                <BarChart
                  layout="vertical"
                  data={sortedUsers}
                  margin={{ left: 0, right: 10 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke="#f1f5f9"
                  />

                  <XAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94a3b8", fontSize: 11 }}
                  />

                  <YAxis
                    dataKey="FirstName"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#374151", fontSize: 12, fontWeight: 500 }}
                    width={80}
                  />

                  <Tooltip contentStyle={styles.tooltip} />

                  <Bar
                    dataKey="hits"
                    fill="#3b82f6"
                    radius={[0, 4, 4, 0]}
                    barSize={16}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Endpoint Distribution + Hourly Peak ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            {/* Endpoint Distribution */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Endpoint Distribution</h3>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px 12px",
                  marginBottom: 4,
                }}
              >
                {(endpointDistribution || []).map((e, i) => (
                  <span
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 11,
                      color: "#64748b",
                      fontWeight: 500,
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: COLORS[i % COLORS.length],
                        display: "inline-block",
                      }}
                    />
                    {e.name}
                  </span>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={endpointDistribution || []}
                    cx="50%"
                    cy="55%"
                    outerRadius={90}
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {(endpointDistribution || []).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={styles.tooltip} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Hourly Peak Load */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Hourly Peak Load</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={timeTrends || []} margin={{ bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="hour"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94a3b8", fontSize: 10 }}
                    tickFormatter={(h) => `${h}:00`}
                    interval={3}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94a3b8", fontSize: 11 }}
                  />
                  <Tooltip contentStyle={styles.tooltip} />
                  <Bar
                    dataKey="hits"
                    fill="#3b82f6"
                    radius={[3, 3, 0, 0]}
                    barSize={10}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Daily Activity Heatmap ── */}
          <div style={{ ...styles.card, marginBottom: 16 }}>
            <h3 style={{ ...styles.cardTitle, marginBottom: 16 }}>
              Daily Activity Heatmap (last 28 days)
            </h3>
            <div style={{ overflowX: "auto" }}>
              <div style={{ display: "flex", gap: 4, minWidth: 500 }}>
                {/* Day labels column */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    paddingTop: 20,
                  }}
                >
                  {DAY_LABELS.map((d) => (
                    <div
                      key={d}
                      style={{
                        fontSize: 11,
                        color: "#94a3b8",
                        height: 16,
                        lineHeight: "16px",
                        width: 28,
                      }}
                    >
                      {d}
                    </div>
                  ))}
                </div>
                {/* Weeks */}
                {heatmapWeeks.map((week, wi) => (
                  <div
                    key={wi}
                    style={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "#94a3b8",
                        height: 16,
                        textAlign: "center",
                      }}
                    >
                      {week[0]
                        ? `${week[0].date.toLocaleString("default", { month: "short" })} ${week[0].date.getDate()}`
                        : ""}
                    </div>
                    {week.map((cell, di) => (
                      <div
                        key={di}
                        title={`${cell.date.toDateString()}: ${cell.count} hits`}
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 3,
                          background: heatColor(cell.count, heatMax),
                          cursor: "default",
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              {/* Legend */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginTop: 10,
                }}
              >
                <span
                  style={{ fontSize: 11, color: "#94a3b8", marginRight: 4 }}
                >
                  Less
                </span>
                {["#e2e8f0", "#bfdbfe", "#60a5fa", "#2563eb", "#1e3a8a"].map(
                  (c) => (
                    <div
                      key={c}
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: c,
                      }}
                    />
                  ),
                )}
                <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 4 }}>
                  More
                </span>
              </div>
            </div>
          </div>

          {/* ── Recent Activity Table ── */}
          <div style={styles.card}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <h3 style={styles.cardTitle}>Recent System Activity</h3>
              <div style={{ display: "flex", gap: 6 }}>
                {["All", "GET", "POST", "PUT", "DELETE"].map((m) => (
                  <button
                    key={m}
                    onClick={() => handleMethodFilter(m)}
                    style={{
                      ...styles.tabBtn,
                      background:
                        methodFilter === m ? "#1e293b" : "transparent",
                      color: methodFilter === m ? "#fff" : "#64748b",
                      borderColor: methodFilter === m ? "#1e293b" : "#e2e8f0",
                    }}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                    {[
                      { label: "Date", field: "date", sortable: true },
                      { label: "Time (EST)", field: "time", sortable: true },
                      { label: "User", field: "user", sortable: true },
                      { label: "Endpoint", field: "endpoint", sortable: false },
                      { label: "Method", field: "method", sortable: false },
                      { label: "Status", field: "status", sortable: false },
                    ].map(({ label, field, sortable }) => (
                      <th
                        key={field}
                        onClick={sortable ? () => handleSort(field) : undefined}
                        style={{
                          ...styles.th,
                          cursor: sortable ? "pointer" : "default",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          {label}
                          {sortable && getSortIcon(field)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedActivity.map((row, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: "1px solid #f8fafc",
                        background: i % 2 === 0 ? "#fff" : "#fafbfc",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f0f7ff")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          i % 2 === 0 ? "#fff" : "#fafbfc")
                      }
                    >
                      <td
                        style={{
                          ...styles.td,
                          whiteSpace: "nowrap",
                          color: "#64748b",
                          fontSize: 13,
                        }}
                      >
                        {new Date(row.timestamp).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "2-digit",
                        })}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          whiteSpace: "nowrap",
                          color: "#64748b",
                          fontSize: 13,
                        }}
                      >
                        {`${new Date(row.timestamp).toLocaleTimeString("en-US", {
                          timeZone: "America/New_York",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        })} EST`}
                      </td>
                      <td style={{ ...styles.td, fontWeight: 600 }}>
                        {row.FirstName}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          fontFamily: "monospace",
                          fontSize: 12,
                          color: "#374151",
                          maxWidth: 280,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.Endpoint}
                      >
                        {row.Endpoint}
                      </td>
                      <td style={styles.td}>
                        <MethodBadge method={row.Method} />
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          color: "#16a34a",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {row.status || 200}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 16,
                paddingTop: 14,
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
                Showing page {currentPage} of {totalPages}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    ...styles.tabBtn,
                    background: currentPage === 1 ? "#f1f5f9" : "transparent",
                    color: currentPage === 1 ? "#94a3b8" : "#64748b",
                    borderColor: "#e2e8f0",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === 1 ? 0.6 : 1,
                  }}
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  style={{
                    ...styles.tabBtn,
                    background:
                      currentPage === totalPages ? "#f1f5f9" : "transparent",
                    color: currentPage === totalPages ? "#94a3b8" : "#64748b",
                    borderColor: "#e2e8f0",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages ? 0.6 : 1,
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {/* ── Call Feedback Details ── */}
          <div style={{ ...styles.card, marginTop: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <h3 style={styles.cardTitle}>Call Feedback Details</h3>
              <div style={{ display: "flex", gap: 12 }}>
                <input
                  type="text"
                  placeholder="Search feedback..."
                  value={feedbackSearchTerm}
                  onChange={(e) => setFeedbackSearchTerm(e.target.value)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    fontSize: 13,
                    outline: "none",
                    width: 240,
                  }}
                />
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                    {[
                      "Date",
                      "Member ID",
                      "Feedback Type",
                      "User Details",
                      "Observation",
                      "Action",
                    ].map((h) => (
                      <th key={h} style={styles.th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(feedbackTable || [])
                    .filter(
                      (row) =>
                        (row.Feedback || "")
                          .toLowerCase()
                          .includes(feedbackSearchTerm.toLowerCase()) ||
                        (row.Observation || "")
                          .toLowerCase()
                          .includes(feedbackSearchTerm.toLowerCase()) ||
                        (row.UserDetails || "")
                          .toLowerCase()
                          .includes(feedbackSearchTerm.toLowerCase()) ||
                        (row.CustomerID || "")
                          .toLowerCase()
                          .includes(feedbackSearchTerm.toLowerCase()),
                    )
                    .map((row, i) => (
                      <tr
                        key={i}
                        style={{
                          borderBottom: "1px solid #f8fafc",
                          background: i % 2 === 0 ? "#fff" : "#fafbfc",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f0f7ff")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            i % 2 === 0 ? "#fff" : "#fafbfc")
                        }
                      >
                        <td
                          style={{
                            ...styles.td,
                            whiteSpace: "nowrap",
                            color: "#64748b",
                            fontSize: 13,
                          }}
                        >
                          {new Date(row.CreatedAt).toLocaleString()}
                        </td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>
                          {row.CustomerID}
                        </td>
                        <td style={styles.td}>
                          <span
                            style={{
                              background: row.Feedback?.includes("Enhancement")
                                ? "#f0fdf4"
                                : "#eff6ff",
                              color: row.Feedback?.includes("Enhancement")
                                ? "#16a34a"
                                : "#2563eb",
                              padding: "2px 8px",
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {row.Feedback}
                          </span>
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            fontSize: 12,
                            color: "#64748b",
                          }}
                        >
                          {row.UserDetails}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            fontSize: 13,
                            color: "#374151",
                            maxWidth: 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.Observation}
                        </td>
                        <td style={styles.td}>
                          <button
                            onClick={() => setSelectedFeedback(row)}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#3b82f6",
                              cursor: "pointer",
                              padding: 4,
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="View Full Observation"
                          >
                            <FiEye size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Feedback Detail Modal ── */}
          {selectedFeedback && (
            <div
              style={styles.modalOverlay}
              onClick={() => setSelectedFeedback(null)}
            >
              <div
                style={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 20,
                  }}
                >
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                    Feedback Observation
                  </h3>
                  <button
                    onClick={() => setSelectedFeedback(null)}
                    style={styles.closeBtn}
                  >
                    <FiX size={20} />
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "100px 1fr",
                    gap: "12px 20px",
                    marginBottom: 20,
                  }}
                >
                  <span style={styles.modalLabel}>Date:</span>
                  <span>
                    {new Date(selectedFeedback.CreatedAt).toLocaleString()}
                  </span>

                  <span style={styles.modalLabel}>Member ID:</span>
                  <span style={{ fontWeight: 600 }}>
                    {selectedFeedback.CustomerID}
                  </span>

                  <span style={styles.modalLabel}>Type:</span>
                  <span
                    style={{
                      background: selectedFeedback.Feedback?.includes(
                        "Enhancement",
                      )
                        ? "#f0fdf4"
                        : "#eff6ff",
                      color: selectedFeedback.Feedback?.includes("Enhancement")
                        ? "#16a34a"
                        : "#2563eb",
                      padding: "2px 8px",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 700,
                      width: "fit-content",
                    }}
                  >
                    {selectedFeedback.Feedback}
                  </span>

                  <span style={styles.modalLabel}>User:</span>
                  <span style={{ color: "#64748b", fontSize: 13 }}>
                    {selectedFeedback.UserDetails}
                  </span>
                </div>
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 16 }}>
                  <label
                    style={{
                      ...styles.modalLabel,
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    Full Observation:
                  </label>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: "#374151",
                      whiteSpace: "pre-wrap",
                      background: "#f8fafc",
                      padding: 16,
                      borderRadius: 8,
                      maxHeight: "40vh",
                      overflowY: "auto",
                    }}
                  >
                    {selectedFeedback.Observation}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Body>
    </div>
  );
};

// ── StatCard Sub-component ────────────────────────────────────────
function StatCard({
  title,
  value,
  trend,
  trendType,
  sparkData,
  sparkColor,
  highlighted,
}) {
  const trendColor =
    trendType === "up"
      ? "#16a34a"
      : trendType === "down"
        ? "#dc2626"
        : "#d97706";
  const trendSymbol =
    trendType === "up" ? "↑" : trendType === "warn" ? "↑" : "↓";

  return (
    <div
      style={{
        background: "#fff",
        border: highlighted ? "2px solid #3b82f6" : "1px solid #e8eaed",
        borderRadius: 14,
        padding: "18px 20px",
        boxShadow: highlighted
          ? "0 0 0 3px rgba(59,130,246,0.1), 0 2px 8px rgba(0,0,0,0.06)"
          : "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: "#64748b",
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: "#0f172a",
          lineHeight: 1,
          marginBottom: 6,
        }}
      >
        {value ?? "—"}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: trendColor }}>
        {trendSymbol} {trend}
      </div>
      <Sparkline data={sparkData} color={sparkColor || "#3b82f6"} />
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────
const styles = {
  card: {
    background: "#fff",
    border: "1px solid #e8eaed",
    borderRadius: 14,
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
  },
  tooltip: {
    borderRadius: 8,
    border: "none",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    fontSize: 13,
  },
  tabBtn: {
    border: "1px solid #e2e8f0",
    borderRadius: 7,
    padding: "4px 12px",
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  filterLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 4,
  },
  select: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "9px 36px 9px 14px",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    color: "#0f172a",
    cursor: "pointer",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    minWidth: 200,
  },
  th: {
    textAlign: "left",
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  td: {
    padding: "11px 14px",
    fontSize: 13,
    color: "#1e293b",
    verticalAlign: "middle",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(15, 23, 42, 0.65)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  },
  modalContent: {
    background: "#fff",
    width: "90%",
    maxWidth: 600,
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    position: "relative",
  },
  modalLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  closeBtn: {
    background: "#f1f5f9",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748b",
    transition: "all 0.2s",
  },
};

export default AnalyticsScreen;
